import { z } from "zod";

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 25;
export const USERNAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{2,24}$/;

/** One code per rule so clients can localize the message. */
export const UsernameFormatCode = z.enum([
  "required",
  "too_short",
  "too_long",
  "invalid_chars",
  "must_start_with_letter",
  "edge_underscore",
  "consecutive_underscore",
]);
export type UsernameFormatCode = z.infer<typeof UsernameFormatCode>;

/** The first rule the value breaks, or `null` when it is a valid username. */
export function usernameFormatCode(value: string): UsernameFormatCode | null {
  if (value.length === 0) return "required";
  if (value.length < USERNAME_MIN_LENGTH) return "too_short";
  if (value.length > USERNAME_MAX_LENGTH) return "too_long";
  if (!/^[A-Za-z0-9_]+$/.test(value)) return "invalid_chars";
  if (!/^[A-Za-z]/.test(value)) return "must_start_with_letter";
  if (/^_|_$/.test(value)) return "edge_underscore";
  if (/__/.test(value)) return "consecutive_underscore";
  return null;
}

export const Username = z
  .string()
  .trim()
  .superRefine((value, ctx) => {
    const code = usernameFormatCode(value);
    if (code) ctx.addIssue({ code: "custom", message: code, params: { code } });
  })
  .meta({
    id: "Username",
    description:
      "A public username: 3–25 letters, digits, or underscores; starts with a letter; no leading, trailing, or doubled underscore. Case is kept for display and ignored for uniqueness.",
    example: "Grace_Hopper",
    minLength: USERNAME_MIN_LENGTH,
    maxLength: USERNAME_MAX_LENGTH,
    pattern: USERNAME_PATTERN.source,
  });
export type Username = z.infer<typeof Username>;

/** The uniqueness key: `Grace` and `grace` are the same name. */
export function normalizeUsername(username: string): string {
  return username.toLowerCase();
}
